import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { getFirebaseAuth, readFirebaseEnv } from './firebase';
import { api } from './api';
import { SessionContext, type Membership, type SessionState } from './sessionContext';

const ACTIVE_ORG_KEY = 'qrcl.activeOrg';
// Clave de antes del renombre (D-180): se lee una vez para no perder la organización elegida.
const LEGACY_ACTIVE_ORG_KEY = 'kuerre.activeOrg'; // marca-anterior: migración

function readStoredOrg(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY) ?? localStorage.getItem(LEGACY_ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(readStoredOrg);

  const refreshMemberships = useCallback(async () => {
    const data = await api.get<{ memberships: Membership[] }>('/v1/me');
    setMemberships(data.memberships ?? []);
  }, []);

  useEffect(() => {
    if (!readFirebaseEnv()) {
      setStatus('signed-out');
      return;
    }
    return onAuthStateChanged(getFirebaseAuth(), async (u) => {
      setUser(u);
      if (u) setStatus('loading');
      if (!u) {
        setMemberships([]);
        setStatus('signed-out');
        return;
      }
      try {
        await refreshMemberships();
      } catch {
        setMemberships([]);
      }
      setStatus('signed-in');
    });
  }, [refreshMemberships]);

  const setActiveOrg = useCallback((orgId: string) => {
    setActiveOrgId(orgId);
    try {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    } catch {
      /* almacenamiento no disponible: la selección vive solo en memoria */
    }
  }, []);

  const logout = useCallback(async () => {
    if (readFirebaseEnv()) await signOut(getFirebaseAuth());
    setMemberships([]);
    try {
      localStorage.removeItem(ACTIVE_ORG_KEY);
      localStorage.removeItem(LEGACY_ACTIVE_ORG_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const activeOrg = useMemo(
    () => memberships.find((m) => m.orgId === activeOrgId) ?? memberships[0] ?? null,
    [memberships, activeOrgId],
  );

  const value = useMemo<SessionState>(
    () => ({ status, user, memberships, activeOrg, setActiveOrg, refreshMemberships, logout }),
    [status, user, memberships, activeOrg, setActiveOrg, refreshMemberships, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
